import { Navbar } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { SubtleWaves } from "@/components/ui/waves";
import { FileText, Globe, ShieldCheck, Mail } from "lucide-react";
import { Seo } from "@/components/Seo";

export const AlgemeneVoorwaardenPage = () => {
  return (
    <>
      <Seo
        title="Algemene Voorwaarden — Lunssence"
        description="De algemene voorwaarden van Lunssence: jouw rechten en onze afspraken bij het bestellen van premium blauwe lotus thee."
        path="/algemene-voorwaarden"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <SubtleWaves />
        <div className="absolute inset-0 bg-gradient-lavender opacity-30" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-foreground mb-6 animate-fade-in-up text-glow">
              Algemene Voorwaarden
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in-up delay-200">
              Terms & Conditions
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative py-20 bg-gradient-to-b from-background to-emerald-50/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-16">
            
            {/* Nederlands Section */}
            <div className="bg-card rounded-2xl border-2 border-gold/20 p-8 md:p-12 shadow-lg">
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-gold" />
                <h2 className="font-serif text-3xl font-bold text-foreground">Nederlands 🇳🇱</h2>
              </div>
              
              <div className="prose prose-lg max-w-none space-y-6">
                <p className="text-muted-foreground leading-relaxed">
                  Welkom bij Lunssence. Door een bestelling te plaatsen op onze website gaat u akkoord met onderstaande voorwaarden.
                </p>

                <div className="space-y-6 mt-8">
                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      1. Toepassing
                    </h3>
                    <p className="text-muted-foreground">
                      Deze voorwaarden gelden voor alle aanbiedingen, bestellingen en overeenkomsten tussen Lunssence en haar klanten.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      2. Productinformatie
                    </h3>
                    <p className="text-muted-foreground">
                      Onze producten worden met zorg samengesteld. Kleine kleur- of vormafwijkingen kunnen voorkomen door natuurlijke ingrediënten of scherminstellingen.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      3. Prijzen & Betaling
                    </h3>
                    <p className="text-muted-foreground">
                      Alle prijzen zijn inclusief btw. Betaling via iDEAL, Klarna of PayPal. Bestellingen worden verzonden na betaling.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      4. Levering
                    </h3>
                    <p className="text-muted-foreground">
                      Levertijd binnen Nederland: 2–5 werkdagen. Internationale bestellingen kunnen langer duren.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      5. Aansprakelijkheid
                    </h3>
                    <p className="text-muted-foreground">
                      Lunssence is niet aansprakelijk voor schade door verkeerd gebruik of allergische reacties. Onze producten vervangen geen medisch advies.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      6. Overmacht
                    </h3>
                    <p className="text-muted-foreground">
                      Bij onvoorziene omstandigheden kan Lunssence levering uitstellen of annuleren.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      7. Geschillen
                    </h3>
                    <p className="text-muted-foreground">
                      Op alle overeenkomsten is Nederlands recht van toepassing.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* English Section */}
            <div className="bg-card rounded-2xl border-2 border-gold/20 p-8 md:p-12 shadow-lg">
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-gold" />
                <h2 className="font-serif text-3xl font-bold text-foreground">English 🇬🇧</h2>
              </div>
              
              <div className="prose prose-lg max-w-none space-y-6">
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to Lunssence. By placing an order on our website, you agree to the following terms.
                </p>

                <div className="space-y-6 mt-8">
                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      1. Applicability
                    </h3>
                    <p className="text-muted-foreground">
                      These terms apply to all offers, orders, and agreements between Lunssence and its customers.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      2. Product Information
                    </h3>
                    <p className="text-muted-foreground">
                      Products are presented carefully. Minor variations may occur due to natural ingredients or screen settings.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      3. Prices & Payment
                    </h3>
                    <p className="text-muted-foreground">
                      All prices include VAT. Payment via iDEAL, Klarna, or PayPal. Orders are shipped after payment.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      4. Delivery
                    </h3>
                    <p className="text-muted-foreground">
                      Delivery within the Netherlands: 2–5 business days. International orders may take longer.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      5. Liability
                    </h3>
                    <p className="text-muted-foreground">
                      Lunssence is not liable for damages due to misuse or allergic reactions. Products do not replace medical advice.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      6. Force Majeure
                    </h3>
                    <p className="text-muted-foreground">
                      In unforeseen circumstances, Lunssence may postpone or cancel delivery.
                    </p>
                  </div>

                  <div className="border-l-4 border-gold pl-6">
                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      7. Disputes
                    </h3>
                    <p className="text-muted-foreground">
                      Dutch law applies to all agreements.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-br from-gold/10 to-emerald-50/30 rounded-2xl p-8 md:p-12 border border-gold/20">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-6 h-6 text-gold" />
                <h3 className="font-serif text-2xl font-bold text-foreground">Contact</h3>
              </div>
              <div className="space-y-2 text-muted-foreground">
                <p><strong className="text-foreground">Lunssence</strong></p>
                <p>Amsterdam, Nederland</p>
                <p>
                  <a href="mailto:info@lunssence.com" className="text-gold hover:underline">
                    info@lunssence.com
                  </a>
                </p>
                <p>
                  <a href="https://www.lunssence.com" className="text-gold hover:underline" target="_blank" rel="noopener noreferrer">
                    www.lunssence.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};
